import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Alert,
  Modal,
  TextInput,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { GymChallenge, Profile } from '@/types/database';
import { Trophy, Award, Users } from 'lucide-react-native';
import { FLAGS } from '@/lib/featureFlags';

interface ParticipantWithProfile {
  id: string;
  user_id: string;
  joined_at: string;
  profile?: Profile;
}

export default function ChallengeParticipantsScreen() {
  const { gymId, challengeId } = useLocalSearchParams<{ gymId: string; challengeId: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { theme } = useTheme();

  const [challenge, setChallenge] = useState<GymChallenge | null>(null);
  const [participants, setParticipants] = useState<ParticipantWithProfile[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [selectedWinnerId, setSelectedWinnerId] = useState<string | null>(null);
  const [winnerNote, setWinnerNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // V1: Challenges disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_CHALLENGES_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    loadData();
  }, [challengeId, gymId]);

  const loadData = async () => {
    if (!challengeId || !gymId) return;

    setLoading(true);
    setError('');

    try {
      const { data: challengeData, error: challengeError } = await supabase
        .from('gym_challenges')
        .select('*')
        .eq('id', challengeId)
        .single();

      if (challengeError) throw challengeError;
      setChallenge(challengeData);

      const { data: membershipData } = await supabase
        .from('gym_memberships')
        .select('role')
        .eq('gym_id', gymId)
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .maybeSingle();

      setIsOwner(membershipData?.role === 'owner');

      const { data: participantsData, error: participantsError } = await supabase
        .from('gym_challenge_participants')
        .select(`
          id,
          user_id,
          joined_at,
          profiles:user_id (
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('challenge_id', challengeId)
        .order('joined_at', { ascending: false });

      if (participantsError) throw participantsError;

      const mapped = participantsData.map((p: any) => ({
        id: p.id,
        user_id: p.user_id,
        joined_at: p.joined_at,
        profile: p.profiles,
      }));

      setParticipants(mapped);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkWinner = (userId: string) => {
    setSelectedWinnerId(userId);
    setWinnerNote('');
    setShowWinnerModal(true);
  };

  const handleSubmitWinner = async () => {
    if (!selectedWinnerId || !challenge) return;

    setSubmitting(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('gym_challenges')
        .update({
          winner_user_id: selectedWinnerId,
          winner_note: winnerNote.trim() || null,
          winner_selected_at: new Date().toISOString(),
        })
        .eq('id', challenge.id);

      if (updateError) throw updateError;

      Alert.alert('Marked', 'Winner Set');
      setShowWinnerModal(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getDisplayName = (profile?: Profile) => {
    if (!profile) return 'Member';
    return profile.display_name || profile.handle || 'Member';
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (!challenge) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.text }]}>Challenge not found</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 24 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>PARTICIPANTS</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {error ? <Text style={[styles.errorMessage, { color: theme.colors.accent, backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>{error}</Text> : null}

          <View style={[styles.challengeInfo, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Trophy size={24} color={theme.colors.accent} />
            <Text style={[styles.challengeTitle, { color: theme.colors.text }]}>
              {challenge.title}
            </Text>
            <View style={styles.infoRow}>
              <Users size={16} color={theme.colors.textSecondary} />
              <Text style={[styles.infoText, { color: theme.colors.textSecondary }]}>
                {participants.length} participant{participants.length !== 1 ? 's' : ''}
              </Text>
            </View>
          </View>

          {challenge.winner_user_id && (
            <View style={[styles.winnerBanner, { backgroundColor: theme.colors.accent + '20', borderColor: theme.colors.accent }]}>
              <Trophy size={20} color={theme.colors.accent} />
              <View style={styles.winnerInfo}>
                <Text style={[styles.winnerLabel, { color: theme.colors.accent }]}>Winner</Text>
                <Text style={[styles.winnerName, { color: theme.colors.text }]}>
                  {getDisplayName(participants.find((p) => p.user_id === challenge.winner_user_id)?.profile)}
                </Text>
                {challenge.winner_note && (
                  <Text style={[styles.winnerNote, { color: theme.colors.textSecondary }]}>
                    {challenge.winner_note}
                  </Text>
                )}
              </View>
            </View>
          )}

          {participants.length === 0 ? (
            <View style={[styles.emptyCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <Users size={32} color={theme.colors.textTertiary} />
              <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                No participants yet
              </Text>
            </View>
          ) : (
            <View style={styles.participantsList}>
              {participants.map((participant) => {
                const isWinner = challenge.winner_user_id === participant.user_id;

                return (
                  <View
                    key={participant.id}
                    style={[
                      styles.participantCard,
                      {
                        backgroundColor: theme.colors.surface,
                        borderColor: isWinner ? theme.colors.accent : theme.colors.border,
                      },
                    ]}>
                    <View style={styles.participantInfo}>
                      <Text style={[styles.participantName, { color: theme.colors.text }]}>
                        {getDisplayName(participant.profile)}
                      </Text>
                      <Text style={[styles.participantDate, { color: theme.colors.textSecondary }]}>
                        Joined {new Date(participant.joined_at).toLocaleDateString()}
                      </Text>
                    </View>
                    {isWinner && (
                      <View style={[styles.winnerBadge, { backgroundColor: theme.colors.accent }]}>
                        <Trophy size={14} color={theme.colors.text} />
                      </View>
                    )}
                    {isOwner && !challenge.winner_user_id && (
                      <TouchableOpacity
                        style={[styles.markWinnerButton, { backgroundColor: theme.colors.accent }]}
                        onPress={() => handleMarkWinner(participant.user_id)}>
                        <Award size={16} color={theme.colors.text} />
                        <Text style={[styles.markWinnerText, { color: theme.colors.text }]}>
                          Mark Winner
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </ScrollView>

      <Modal visible={showWinnerModal} transparent animationType="fade">
        <View style={[styles.modalOverlay, { backgroundColor: theme.colors.background + 'B3' }]}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Mark Winner</Text>
            <Text style={[styles.modalSubtitle, { color: theme.colors.textSecondary }]}>
              Marking {getDisplayName(participants.find((p) => p.user_id === selectedWinnerId)?.profile)} as the winner
            </Text>

            <View style={styles.inputContainer}>
              <Text style={[styles.inputLabel, { color: theme.colors.text }]}>
                Winner Note (Optional)
              </Text>
              <TextInput
                style={[
                  styles.textArea,
                  {
                    backgroundColor: theme.colors.background,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder="Add a note about this win..."
                placeholderTextColor={theme.colors.textTertiary}
                value={winnerNote}
                onChangeText={setWinnerNote}
                editable={!submitting}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.colors.border }]}
                onPress={() => setShowWinnerModal(false)}
                disabled={submitting}>
                <Text style={[styles.modalButtonText, { color: theme.colors.text }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.colors.accent }]}
                onPress={handleSubmitWinner}
                disabled={submitting}>
                {submitting ? (
                  <ActivityIndicator color={theme.colors.text} size="small" />
                ) : (
                  <Text style={[styles.modalButtonText, { color: theme.colors.text }]}>
                    Confirm
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1.5,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  content: {
    gap: 20,
  },
  challengeInfo: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 20,
    alignItems: 'center',
    gap: 12,
  },
  challengeTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  infoText: {
    fontSize: 14,
  },
  winnerBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderRadius: 2,
    borderWidth: 2,
  },
  winnerInfo: {
    flex: 1,
    gap: 4,
  },
  winnerLabel: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  winnerName: {
    fontSize: 18,
    fontWeight: '700',
  },
  winnerNote: {
    fontSize: 14,
    fontStyle: 'italic',
  },
  emptyCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  participantsList: {
    gap: 12,
  },
  participantCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 2,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  participantInfo: {
    flex: 1,
    gap: 4,
  },
  participantName: {
    fontSize: 16,
    fontWeight: '700',
  },
  participantDate: {
    fontSize: 12,
  },
  winnerBadge: {
    padding: 8,
    borderRadius: 2,
  },
  markWinnerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 2,
  },
  markWinnerText: {
    fontSize: 13,
    fontWeight: '700',
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    padding: 20,
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 2,
    padding: 24,
    gap: 20,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '900',
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  inputContainer: {
    gap: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '700',
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    minHeight: 80,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});
